import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const FIELDS = [
  { key: 'legal_name', label: 'Legal Business Name' },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'fssai', label: 'FSSAI Number' },
  { key: 'state_code', label: 'State Code' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'default_gst_rate', label: 'Default GST Rate (%)', type: 'number' },
  { key: 'logo_url', label: 'Logo URL' },
  { key: 'upi_id', label: 'UPI ID (VPA) — e.g. yourname@okhdfc' },
  { key: 'upi_name', label: 'UPI Payee Name (shown on customer app)' },
  { key: 'upi_qr_url', label: 'UPI QR Image URL (optional static QR)' },
];

export default function Settings({ user, onLogout }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/business`, auth()).then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await axios.put(`${API}/settings/business`, { ...form, default_gst_rate: parseFloat(form.default_gst_rate) }, auth());
      toast.success('Business settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  if (!form) return <Layout user={user} onLogout={onLogout}><div className="p-8 text-stone-400">Loading...</div></Layout>;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 max-w-3xl">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-stone-900">Business Settings</h1>
          <p className="text-stone-600 mt-1">These details appear on GST invoices</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6 space-y-5">
          {form.logo_url && <img src={form.logo_url} alt="logo" className="h-16 object-contain" />}
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-stone-700 mb-1">{f.label}</label>
              <Input
                data-testid={`settings-${f.key}`}
                type={f.type || 'text'}
                value={form[f.key] ?? ''}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Address</label>
            <textarea
              data-testid="settings-address"
              value={form.address || ''}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full border border-stone-200 rounded-sm p-3 focus:outline-none focus:ring-2 focus:ring-primary"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Invoice Footer</label>
            <Input data-testid="settings-invoice_footer" value={form.invoice_footer || ''} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })} />
          </div>
          <Button data-testid="save-settings-button" onClick={save} className="bg-primary text-white">
            <Save size={18} className="mr-2" /> Save Settings
          </Button>
        </div>
      </div>
    </Layout>
  );
}
